import React, { useState, useRef } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Skeleton,
  Alert
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import CustomAxios from "../utils/CustomAxios";

export function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastFailedInput, setLastFailedInput] = useState(null);
  const chatContainerRef = useRef(null);

  const handleSendMessage = async () => {
    if (input.trim() === '') return;

    const newMessages = [...messages, { sender: 'user', text: input }];
    setMessages(newMessages);
    setIsLoading(true);
    setLastFailedInput(null);

    try {
      const response = await CustomAxios.post('oasis/ai_reporting/', {
        question: input,
      });

      const { query_result, table_header, responseType } = response.data;

      const botMessage = {
        sender: 'bot',
        table: responseType === 'table' ? { query_result, table_header } : null,
      };

      setMessages((prevMessages) => [...prevMessages, botMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          sender: 'bot',
          text: 'Something went wrong. Please try again.',
          error: true
        },
      ]);
      setLastFailedInput(input);
    } finally {
      setInput('');
      setIsLoading(false);
    }
  };

  const downloadTableAsExcel = (tableData, headers) => {
    if (!tableData?.length || !headers?.length) return;
    const wb = XLSX.utils.book_new();
    const wsData = [headers, ...tableData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Report Data');
    XLSX.writeFile(wb, 'report_data.xlsx');
  };

  const downloadTableAsCSV = (tableData, headers) => {
    if (!tableData?.length || !headers?.length) return;
    const csvRows = [headers.join(',')];
    tableData.forEach(row => csvRows.push(row.join(',')));
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'report_data.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadChatAsPdf = () => {
    const doc = new jsPDF();
    let y = 10;
    const margin = 10;
    const pageWidth = doc.internal.pageSize.getWidth() - 2 * margin;

    messages.forEach((msg) => {
      const sender = msg.sender === 'user' ? 'You:' : 'Bot:';
      const content = `${sender} ${msg.text || ''}`;
      const lines = doc.splitTextToSize(content, pageWidth);

      lines.forEach((line) => {
        doc.text(margin, y, line);
        y += 5;
      });

      if (msg.table && Array.isArray(msg.table.table_header) && Array.isArray(msg.table.query_result)) {
        y += 5;
        doc.autoTable({
          head: [msg.table.table_header],
          body: msg.table.query_result,
          startY: y,
          margin: { left: margin, right: margin },
        });
        y = doc.autoTable.previous.finalY + 10;
      } else {
        y += 10;
      }
    });

    doc.save('chat_history.pdf');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '25px', height: '95vh', width: '100%', justifyContent: 'flex-end' }}>
      <Paper
        sx={{ flexGrow: 1, padding: 2, marginBottom: 1, maxHeight: 'calc(90vh - 40px)', overflowY: 'auto', borderRadius: '8px' }}
        ref={chatContainerRef}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>Oasis AI Reporting Assistant</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Ask a question to get live insights from your database.</Typography>
        <Stack spacing={2}>
          {messages.map((msg, idx) => (
            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 1, maxHeight:"400px",overflow:"auto" }}>
              {msg.text && (
                <Typography
                  variant="body1"
                  sx={{
                    backgroundColor: msg.sender === 'user' ? '#1976d2' : '#e3f2fd',
                    color: msg.sender === 'user' ? 'white' : 'black',
                    padding: 1,
                    borderRadius: 2,
                    maxWidth: '60%',
                    wordWrap: 'break-word',
                  }}
                >
                  {msg.text}
                </Typography>
              )}

              {msg.error && lastFailedInput && (
                <Button variant="outlined" size="small" onClick={() => { setInput(lastFailedInput); handleSendMessage(); }} sx={{ mt: 1 }}>
                  Retry
                </Button>
              )}

              {msg.table && Array.isArray(msg.table.table_header) && Array.isArray(msg.table.query_result) ? (
                <Paper sx={{ padding: 2, mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <IconButton onClick={() => downloadTableAsExcel(msg.table.query_result, msg.table.table_header)}>
                      <FileDownloadIcon /> <Typography sx={{ ml: 0.5 }}>.xlsx</Typography>
                    </IconButton>
                    <IconButton onClick={() => downloadTableAsCSV(msg.table.query_result, msg.table.table_header)}>
                      <FileDownloadIcon /> <Typography sx={{ ml: 0.5 }}>.csv</Typography>
                    </IconButton>
                  </Box>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {msg.table.table_header.map((header, i) => <TableCell key={i}>{header}</TableCell>)}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {msg.table.query_result.map((row, i) => (
                        <TableRow key={i}>
                          {row.map((cell, j) => <TableCell key={j}>{cell}</TableCell>)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              ) : (
                msg.sender === 'bot' &&
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Unable to display table. Response data is not in the expected format.
                </Alert>
              )}
            </Box>
          ))}
          {isLoading && <Skeleton variant="rectangular" height={60} animation="wave" />}
        </Stack>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 1 }}>
        <Button onClick={downloadChatAsPdf} sx={{ mr: 1 }} variant="outlined" startIcon={<FileDownloadIcon />}>Download Chat</Button>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          variant="outlined"
          fullWidth
          placeholder="Ask a question..."
          sx={{ marginRight: 1 }}
        />
        <Button
          onClick={handleSendMessage}
          variant="contained"
          color="primary"
          sx={{ height: "35px" }}
          endIcon={<SendIcon />}
          disabled={isLoading || input.trim() === ''}
        >
          {isLoading ? 'Sending...' : 'Send'}
        </Button>
      </Box>
    </Box>
  );
}
